import { ChartFragment } from 'models/api-response';
import { Box, Headline } from 'components/common';
import { ELSFlex, ELSFlexItem } from 'components/common/els';
import { FormField, FormFieldLabel, SectionTitle } from './constants';
import ReadOnlyInput from '../shared/ReadOnlyInput';

export interface DischargeSummaryScreenReadOnlyProps {
  chartFragment: ChartFragment;
  statusFragment: ChartFragment;
  hideHeadline?: boolean;
}

const DischargeSummaryScreenReadOnly = (props: DischargeSummaryScreenReadOnlyProps) => {
  const { chartFragment, statusFragment, hideHeadline } = props;

  return (
    <Box className="discharge-summary-screen-read-only" mt2>
      {!hideHeadline && (
        <>
          <h1>Discharge Summary</h1>
          <Headline statusFragment={statusFragment} chartingFragment={chartFragment} />
        </>
      )}
      <h2>{SectionTitle.DISCHARGE_SUMMARY}</h2>
      <Box className="sng-article">
        {!chartFragment && <h3>Chart Omitted</h3>}
        {chartFragment && (
          <>
            <ELSFlex left gutters className="o-els-flex-layout--gutters-1x1o2 u-els-margin-bottom" wrap>
              <ELSFlexItem w="1o2" md="1o1">
                <ReadOnlyInput
                  chartFragment={chartFragment}
                  sectionTitle={SectionTitle.DISCHARGE_SUMMARY}
                  formFieldId={FormField.ADMISSION_DATE}
                  customTitle={FormFieldLabel.ADMISSION_DATE}
                />
              </ELSFlexItem>
              <ELSFlexItem w="1o2" md="1o1">
                <ReadOnlyInput
                  chartFragment={chartFragment}
                  sectionTitle={SectionTitle.DISCHARGE_SUMMARY}
                  formFieldId={FormField.DISCHARGE_DATE}
                  customTitle={FormFieldLabel.DISCHARGE_DATE}
                />
              </ELSFlexItem>
            </ELSFlex>

            <ELSFlex left gutters className="o-els-flex-layout--gutters-1x1o2 u-els-margin-bottom" wrap>
              <ELSFlexItem w="1o2" md="1o1">
                <h3>{SectionTitle.REASON_FOR_ADMISSION}</h3>
                <ReadOnlyInput
                  chartFragment={chartFragment}
                  sectionTitle={SectionTitle.REASON_FOR_ADMISSION}
                  formFieldId={FormField.PRESENTING_PROBLEM}
                  customTitle={FormFieldLabel.PRESENTING_PROBLEM}
                />
                <ReadOnlyInput
                  chartFragment={chartFragment}
                  sectionTitle={SectionTitle.REASON_FOR_ADMISSION}
                  formFieldId={FormField.PRINCIPAL_DIAGNOSIS}
                  customTitle={FormFieldLabel.PRINCIPAL_DIAGNOSIS}
                />
                <ReadOnlyInput
                  chartFragment={chartFragment}
                  sectionTitle={SectionTitle.REASON_FOR_ADMISSION}
                  formFieldId={FormField.ADDITIONAL_DIAGNOSIS}
                  customTitle={FormFieldLabel.ADDITIONAL_DIAGNOSIS}
                />
                <ReadOnlyInput
                  chartFragment={chartFragment}
                  sectionTitle={SectionTitle.REASON_FOR_ADMISSION}
                  formFieldId={FormField.SUMMARY_OF_HOSPITAL_STAY}
                  customTitle={FormFieldLabel.SUMMARY_OF_HOSPITAL_STAY}
                />
              </ELSFlexItem>

              <ELSFlexItem w="1o2" md="1o1">
                <h3>{SectionTitle.TREATING_TEAM_AND_CONTACT_DETAILS}</h3>
                <ReadOnlyInput
                  chartFragment={chartFragment}
                  sectionTitle={SectionTitle.TREATING_TEAM_AND_CONTACT_DETAILS}
                  formFieldId={FormField.ATTENDING_CLINICIANS}
                  customTitle={FormFieldLabel.ATTENDING_CLINICIANS}
                />
                <ReadOnlyInput
                  chartFragment={chartFragment}
                  sectionTitle={SectionTitle.TREATING_TEAM_AND_CONTACT_DETAILS}
                  formFieldId={FormField.WARD_UNIT}
                  customTitle={FormFieldLabel.WARD_UNIT}
                />
              </ELSFlexItem>
            </ELSFlex>

            <ELSFlex left gutters className="o-els-flex-layout--gutters-1x1o2 u-els-margin-bottom" wrap>
              <ELSFlexItem w="1o2" md="1o1">
                <h3>{SectionTitle.CLINICAL_SUMMARY}</h3>
                <ReadOnlyInput
                  chartFragment={chartFragment}
                  sectionTitle={SectionTitle.CLINICAL_SUMMARY}
                  formFieldId={FormField.KEY_INVESTIGATIONS_AND_RESULTS}
                  customTitle={FormFieldLabel.KEY_INVESTIGATIONS_AND_RESULTS}
                />
                <ReadOnlyInput
                  chartFragment={chartFragment}
                  sectionTitle={SectionTitle.CLINICAL_SUMMARY}
                  formFieldId={FormField.PROCEDURES_PERFORMED}
                  customTitle={FormFieldLabel.PROCEDURES_PERFORMED}
                />
                <ReadOnlyInput
                  chartFragment={chartFragment}
                  sectionTitle={SectionTitle.CLINICAL_SUMMARY}
                  formFieldId={FormField.RESPONSE_TO_TREATMENT}
                  customTitle={FormFieldLabel.RESPONSE_TO_TREATMENT}
                />
                <ReadOnlyInput
                  chartFragment={chartFragment}
                  sectionTitle={SectionTitle.CLINICAL_SUMMARY}
                  formFieldId={FormField.OUTSTANDING_ISSUES}
                  customTitle={FormFieldLabel.OUTSTANDING_ISSUES}
                />
              </ELSFlexItem>

              <ELSFlexItem w="1o2" md="1o1">
                <h3>{SectionTitle.MEDICATIONS}</h3>
                <ReadOnlyInput
                  chartFragment={chartFragment}
                  sectionTitle={SectionTitle.MEDICATIONS}
                  formFieldId={FormField.DISCHARGE_MEDICATIONS}
                  customTitle={FormFieldLabel.DISCHARGE_MEDICATIONS}
                />
                <ReadOnlyInput
                  chartFragment={chartFragment}
                  sectionTitle={SectionTitle.MEDICATIONS}
                  formFieldId={FormField.CHANGES_FROM_ADMISSION_MEDICATIONS}
                  customTitle={FormFieldLabel.CHANGES_FROM_ADMISSION_MEDICATIONS}
                />
                <ReadOnlyInput
                  chartFragment={chartFragment}
                  sectionTitle={SectionTitle.MEDICATIONS}
                  formFieldId={FormField.REASONS_FOR_CHANGES}
                  customTitle={FormFieldLabel.REASONS_FOR_CHANGES}
                />
                <ReadOnlyInput
                  chartFragment={chartFragment}
                  sectionTitle={SectionTitle.MEDICATIONS}
                  formFieldId={FormField.ALLERGIES_AND_ADVERSE_DRUG_REACTIONS}
                  customTitle={FormFieldLabel.ALLERGIES_AND_ADVERSE_DRUG_REACTIONS}
                />
              </ELSFlexItem>
            </ELSFlex>

            <ELSFlex left gutters className="o-els-flex-layout--gutters-1x1o2 u-els-margin-bottom" wrap>
              <ELSFlexItem w="1o2" md="1o1">
                <h3>{SectionTitle.FOLLOW_UP_AND_REFERRALS}</h3>
                <ReadOnlyInput
                  chartFragment={chartFragment}
                  sectionTitle={SectionTitle.FOLLOW_UP_AND_REFERRALS}
                  formFieldId={FormField.APPOINTMENTS}
                  customTitle={FormFieldLabel.APPOINTMENTS}
                />
                <ReadOnlyInput
                  chartFragment={chartFragment}
                  sectionTitle={SectionTitle.FOLLOW_UP_AND_REFERRALS}
                  formFieldId={FormField.REFERRALS_TO}
                  customTitle={FormFieldLabel.REFERRALS_TO}
                />
                <ReadOnlyInput
                  chartFragment={chartFragment}
                  sectionTitle={SectionTitle.FOLLOW_UP_AND_REFERRALS}
                  formFieldId={FormField.REFERRALS_TO_OTHER}
                  customTitle={FormFieldLabel.REFERRALS_TO_OTHER}
                  notRenderBlankField
                />
              </ELSFlexItem>

              <ELSFlexItem w="1o2" md="1o1">
                <h3>{SectionTitle.PATIENT_EDUCATION_AND_INSTRUCTIONS}</h3>
                <ReadOnlyInput
                  chartFragment={chartFragment}
                  sectionTitle={SectionTitle.PATIENT_EDUCATION_AND_INSTRUCTIONS}
                  formFieldId={FormField.DISCHARGE_INSTRUCTIONS}
                  customTitle={FormFieldLabel.DISCHARGE_INSTRUCTIONS}
                />
                <ReadOnlyInput
                  chartFragment={chartFragment}
                  sectionTitle={SectionTitle.PATIENT_EDUCATION_AND_INSTRUCTIONS}
                  formFieldId={FormField.WARNING_SIGNS_TO_WATCH_FOR}
                  customTitle={FormFieldLabel.WARNING_SIGNS_TO_WATCH_FOR}
                />
                <ReadOnlyInput
                  chartFragment={chartFragment}
                  sectionTitle={SectionTitle.PATIENT_EDUCATION_AND_INSTRUCTIONS}
                  formFieldId={FormField.CONTACT_INFORMATION_FOR_URGENT_CONCERNS}
                  customTitle={FormFieldLabel.CONTACT_INFORMATION_FOR_URGENT_CONCERNS}
                />
              </ELSFlexItem>
            </ELSFlex>

            <ELSFlex left gutters className="o-els-flex-layout--gutters-1x1o2 u-els-margin-bottom" wrap>
              <ELSFlexItem w="1o2" md="1o1">
                <h3>{SectionTitle.SUPPORT_SERVICES_AND_EQUIPMENT}</h3>
                <ReadOnlyInput
                  chartFragment={chartFragment}
                  sectionTitle={SectionTitle.SUPPORT_SERVICES_AND_EQUIPMENT}
                  formFieldId={FormField.HOME_CARE_SERVICES_ARRANGED}
                  customTitle={FormFieldLabel.HOME_CARE_SERVICES_ARRANGED}
                />
                <ReadOnlyInput
                  chartFragment={chartFragment}
                  sectionTitle={SectionTitle.SUPPORT_SERVICES_AND_EQUIPMENT}
                  formFieldId={FormField.EQUIPMENT_PROVIDED}
                  customTitle={FormFieldLabel.EQUIPMENT_PROVIDED}
                />
                <ReadOnlyInput
                  chartFragment={chartFragment}
                  sectionTitle={SectionTitle.SUPPORT_SERVICES_AND_EQUIPMENT}
                  formFieldId={FormField.TRANSPORT_ARRANGEMENTS}
                  customTitle={FormFieldLabel.TRANSPORT_ARRANGEMENTS}
                />
              </ELSFlexItem>

              <ELSFlexItem w="1o2" md="1o1">
                <h3>{SectionTitle.CONSENT_AND_COMMUNICATION}</h3>
                <ReadOnlyInput
                  chartFragment={chartFragment}
                  sectionTitle={SectionTitle.CONSENT_AND_COMMUNICATION}
                  formFieldId={FormField.PATIENT_CARER_INVOLVEMENT_IN_DISCHARGE_PLANNING}
                  customTitle={FormFieldLabel.PATIENT_CARER_INVOLVEMENT_IN_DISCHARGE_PLANNING}
                />
                <ReadOnlyInput
                  chartFragment={chartFragment}
                  sectionTitle={SectionTitle.CONSENT_AND_COMMUNICATION}
                  formFieldId={FormField.INTERPRETER_SERVICES_USED}
                  customTitle={FormFieldLabel.INTERPRETER_SERVICES_USED}
                />
                <ReadOnlyInput
                  chartFragment={chartFragment}
                  sectionTitle={SectionTitle.CONSENT_AND_COMMUNICATION}
                  formFieldId={FormField.CONSENT_FOR_INFORMATION_SHARING}
                  customTitle={FormFieldLabel.CONSENT_FOR_INFORMATION_SHARING}
                />
              </ELSFlexItem>
            </ELSFlex>

            <ELSFlex left gutters className="o-els-flex-layout--gutters-1x1o2 u-els-margin-bottom" wrap>
              <ELSFlexItem w="1o2" md="1o1">
                <h3>{SectionTitle.CLINICAL_HANDOVER}</h3>
                <ReadOnlyInput
                  chartFragment={chartFragment}
                  sectionTitle={SectionTitle.CLINICAL_HANDOVER}
                  formFieldId={FormField.HANDOVER_TO_RECEIVING_HEALTHCARE_PROVIDER}
                  customTitle={FormFieldLabel.HANDOVER_TO_RECEIVING_HEALTHCARE_PROVIDER}
                />
              </ELSFlexItem>

              <ELSFlexItem w="1o2" md="1o1">
                <h3>{SectionTitle.CLINICIAN_SIGNATURE}</h3>
                <ReadOnlyInput
                  chartFragment={chartFragment}
                  sectionTitle={SectionTitle.CLINICIAN_SIGNATURE}
                  formFieldId={FormField.CLINICIAN_SIGNATURE_NAME}
                  customTitle={FormFieldLabel.CLINICIAN_SIGNATURE_NAME}
                />
                <ReadOnlyInput
                  chartFragment={chartFragment}
                  sectionTitle={SectionTitle.CLINICIAN_SIGNATURE}
                  formFieldId={FormField.CLINICIAN_SIGNATURE_POSITION}
                  customTitle={FormFieldLabel.CLINICIAN_SIGNATURE_POSITION}
                />
                <ReadOnlyInput
                  chartFragment={chartFragment}
                  sectionTitle={SectionTitle.CLINICIAN_SIGNATURE}
                  formFieldId={FormField.CLINICIAN_SIGNATURE_DATE}
                  customTitle={FormFieldLabel.CLINICIAN_SIGNATURE_DATE}
                />
              </ELSFlexItem>
            </ELSFlex>
          </>
        )}
      </Box>
    </Box>
  );
};

DischargeSummaryScreenReadOnly.displayName = 'DischargeSummaryScreenReadOnly';
export default DischargeSummaryScreenReadOnly;
